import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import { Flags, PublicKeys } from '@/api';
import InputOTP from '@/components/InputOTP';
import { Link } from '@/components/Link/Link';
import Button from '@/design_system/Button';
import InputText from '@/design_system/InputText/InputText';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { MfaMethod, useCompleteMfa, useCreateSession, UserFromGetSession } from '@/models/user';
import { useCurrentSession, useFlags, usePublicKeys } from '@/services/auth';

type CompleteLoginFn = (data: {
  user: UserFromGetSession;
  flags: Flags;
  publicKeys: PublicKeys;
}) => void;

export const LegacyLogin = () => {
  const navigate = useNavigate();

  const { setCurrentSession } = useCurrentSession();
  const { setFlags } = useFlags();
  const { setPublicKeys } = usePublicKeys();

  const [step, setStep] = useState<'login' | 'mfa-email' | 'mfa-expired'>('login');
  const [mfaToken, setMfaToken] = useState<string>();

  const completeLogin: CompleteLoginFn = ({ user, flags, publicKeys }) => {
    setCurrentSession(user);
    setFlags(flags);
    setPublicKeys(publicKeys);

    const redirectTo = new URLSearchParams(window.location.search).get('redirect') ?? '';

    if (redirectTo === '/' || redirectTo.match(/^\/[^/]/)) {
      navigate(redirectTo, { replace: true });

      return;
    }

    navigate('/requests', { replace: true });
  };

  if (step === 'login' || step === 'mfa-expired') {
    return (
      <LoginForm
        onCompleteLogin={completeLogin}
        onMfaRequired={(mfaToken: string, mfaMethod: MfaMethod['type']) => {
          setMfaToken(mfaToken);
          setStep(`mfa-${mfaMethod}`);
        }}
        mfaHasExpired={step === 'mfa-expired'}
      />
    );
  }

  if (step === 'mfa-email' && !!mfaToken) {
    return (
      <MfaEmailForm
        mfaToken={mfaToken}
        onCompleteLogin={completeLogin}
        onMfaExpired={() => setStep('mfa-expired')}
      />
    );
  }
};

const LoginForm = ({
  onCompleteLogin,
  onMfaRequired,
  mfaHasExpired,
}: {
  onCompleteLogin: CompleteLoginFn;
  onMfaRequired: (mfaToken: string, method: 'email') => void;
  mfaHasExpired: boolean;
}) => {
  const { t } = useLingui();

  const {
    mutateAsync: createSession,
    isPending: isCreateSessionPending,
    isSuccess: isCreateSessionSuccess,
  } = useCreateSession();

  const mfaExpiredError = t({
    id: 'login.error.mfa-expired',
    message: 'The 2-step verification token has expired. Please try logging in again.',
  });

  const [error, setError] = useState(mfaHasExpired ? mfaExpiredError : '');
  const [email, setEmail] = useState('');

  const onSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();

    setError('');

    const data = new FormData(evt.target as HTMLFormElement);

    createSession({
      email: data.get('email') as string,
      password: data.get('password') as string,
    })
      .then((response) => {
        if ('mfaToken' in response) {
          // For now there is only one MFA method (email) so the code has already been sent to the user
          // In the future, when there are other methods, we'll allow the user to choose one
          onMfaRequired(response.mfaToken, response.mfaMethods[0].type);
        } else {
          onCompleteLogin(response);
        }
      })
      .catch((err) => {
        if (err.status === 403 && document.cookie.includes('__session_exists')) {
          // The user probably logged in in another tab, let's reload the page to get redirected
          window.location.reload();
        } else if (err.message === 'Invalid credentials') {
          setError(
            t({
              id: 'login.error.incorrect-email-or-password',
              message: 'Email or password incorrect',
            })
          );
        } else {
          console.error(err);
          setError(
            (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
          );
        }
      });
  };

  return (
    <form onSubmit={onSubmit}>
      <Title size="200" as="h1" align="center">
        <Trans id="login.title">Log in to continue</Trans>
      </Title>
      <Stack gap="1.5rem">
        <InputText
          isRequired
          name="email"
          label={<Trans id="login.input.email.label">Email</Trans>}
          type="email"
          inputMode="email"
          placeholder={t({
            id: 'login.input.email.placeholder',
            message: 'Enter your email address...',
          })}
          messageType={error ? 'error' : undefined}
          onChange={setEmail}
        />
        <InputText
          isRequired
          name="password"
          label={<Trans id="login.input.password.label">Password</Trans>}
          type="password"
          placeholder={t({
            id: 'login.input.password.placeholder',
            message: 'Enter your password...',
          })}
          messageType={error ? 'error' : undefined}
        />
        {error && (
          <InlineText weight="medium" color="danger" align="center">
            {error}
          </InlineText>
        )}
        <Link className="text-link" to={`/forgotten-password?email=${encodeURIComponent(email)}`}>
          <Trans id="login.forget-password">Forgot your password?</Trans>
        </Link>
        <Button
          type="submit"
          isLoading={isCreateSessionPending || isCreateSessionSuccess}
          disabled={isCreateSessionPending || isCreateSessionSuccess}
        >
          <Trans id="login.continue">Continue</Trans>
        </Button>
      </Stack>
    </form>
  );
};

const MfaEmailForm = ({
  mfaToken,
  onCompleteLogin,
  onMfaExpired,
}: {
  mfaToken: string;
  onCompleteLogin: CompleteLoginFn;
  onMfaExpired: () => void;
}) => {
  const { t } = useLingui();

  const [code, setCode] = useState('');

  const [error, setError] = useState('');

  const { mutateAsync: completeMfa, isPending: isCompleteMfaPending } = useCompleteMfa();

  const handleVerify = () => {
    if (!code || code.length !== 6) {
      setError(
        t({
          id: 'login.mfa-email-form.error.no-code',
          message: 'Please insert a 6-digit code',
        })
      );

      return;
    }

    completeMfa({ mfaMethod: 'email', mfaToken, code })
      .then(onCompleteLogin)
      .catch((err) => {
        if (err.message.includes('Expired code')) {
          onMfaExpired();
        } else if (err.message.includes('Invalid code')) {
          setError(
            t({
              id: 'login.mfa-email-form.error.invalid-code',
              message: 'The code is invalid, please try again',
            })
          );
        } else {
          console.error(err);
          setError(
            (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
          );
        }
      });
  };

  return (
    <Stack gap="1rem" alignItems="center">
      <Title size="200" as="h1">
        <Trans id="login.mfa-email-form.title">2-step verification</Trans>
      </Title>
      <Paragraph align="center">
        <Trans id="login.mfa-email-form.text">
          We have just sent a 6-digit verification code to your email.
          <br />
          Please insert it below:
        </Trans>
      </Paragraph>
      <InputOTP value={code} onChange={setCode} onComplete={handleVerify} />
      <Button variant="primary" onPress={handleVerify} isLoading={isCompleteMfaPending}>
        <Trans id="login.mfa-email-form.verify">Verify</Trans>
      </Button>
      {!!error && <Message type="error">{error}</Message>}
    </Stack>
  );
};
